import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Anchor, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useLang, Lang } from "@/lib/i18n";

export function Logo({ light = true }: { light?: boolean }) {
  return (
    <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 group">
      <span className="grid place-items-center h-10 w-10 rounded-full border border-[#D4AF37]/60 text-[#D4AF37] transition-transform duration-500 group-hover:rotate-12">
        <Anchor className="h-5 w-5" />
      </span>
      <span className="leading-none">
        <span className={`block font-display text-lg tracking-[0.18em] ${light ? "text-white" : "text-[#0B192C]"}`}>LEGENDARY</span>
        <span className="block text-[10px] uppercase tracking-[0.35em] text-[#D4AF37] mt-1">Vacational Club</span>
      </span>
    </Link>
  );
}

export function LangToggle() {
  const { lang, setLang } = useLang();
  return (
    <div data-testid="language-toggle" className="flex items-center rounded-full border border-white/20 p-0.5 text-xs font-semibold">
      {(["es", "en"] as Lang[]).map((l) => (
        <button key={l} data-testid={`lang-${l}-btn`} onClick={() => setLang(l)}
          className={`px-3 py-1.5 rounded-full uppercase tracking-wider transition-colors ${lang === l ? "bg-[#D4AF37] text-[#0B192C]" : "text-white/70 hover:text-white"}`}>
          {l}
        </button>
      ))}
    </div>
  );
}

export default function Header() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const links = [
    { to: "/yachts", label: t("nav.yachts"), id: "nav-yachts" },
    { to: "/tours", label: t("nav.tours"), id: "nav-tours" },
  ];
  const cls = ({ isActive }: { isActive: boolean }) =>
    `text-sm tracking-wide transition-colors ${isActive ? "text-[#00D2C8]" : "text-white/80 hover:text-white"}`;
  return (
    <header className="sticky top-0 z-40 bg-[#0B192C]/95 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <Logo />
        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => <NavLink key={l.to} to={l.to} className={cls} data-testid={l.id}>{l.label}</NavLink>)}
          <a href="#contacto" className="text-sm text-white/80 hover:text-white transition-colors" data-testid="nav-contact">{t("nav.contact")}</a>
        </nav>
        <div className="flex items-center gap-3">
          <LangToggle />
          <Link to="/yachts" data-testid="header-book-btn"
            className="hidden md:inline-flex items-center rounded-full bg-[#D4AF37] px-5 py-2.5 text-sm font-semibold text-[#0B192C] transition-colors hover:bg-[#F3E5AB]">
            {t("nav.book")}
          </Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="md:hidden text-white p-2" data-testid="mobile-menu-btn" aria-label="Menu"><Menu className="h-6 w-6" /></button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#0B192C] border-white/10 text-white">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="mt-10 flex flex-col gap-6">
                {links.map((l) => (
                  <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-${l.id}`} className="font-display text-2xl">{l.label}</NavLink>
                ))}
                <a href="#contacto" onClick={() => setOpen(false)} className="font-display text-2xl" data-testid="mobile-nav-contact">{t("nav.contact")}</a>
                <Link to="/yachts" onClick={() => setOpen(false)} className="mt-4 rounded-full bg-[#D4AF37] px-5 py-3 text-center font-semibold text-[#0B192C]">{t("nav.book")}</Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
